import React from 'react';
import { VersionHistorySummaryGridSkeleton } from '../VersionHistorySummaryGrid';
import {
  EpdVersionHistoryData,
  useEpdVersionHistoryDataOrWhileLoadingThenNull,
} from '../utils/EpdVersionHistoryDataContext';
import VersionHistorySection from '../VersionHistorySection';
import VersionHistoryScenarioGrid from './VersionHistoryScenarioGrid';
import ModuleGridPdf from '../../grids/scenarios/ModuleGridPdf';
import { VersionStateType } from 'services/EpdClient';
import { PdfH2 } from 'pages/v2/compiler/PDF/Styles.styled';
import { ScenarioItemModel } from 'types/types';

export interface ScenarioModuleItem {
  moduleId: string;
  dataGridCaption: string;
  dataGridModuleName: string;
  descriptionLabel: string;
}

const VersionHistoryScenariosSection: React.FC<{}> = () => {
  const data = useEpdVersionHistoryDataOrWhileLoadingThenNull();

  return (
    <>
      {data?.scenarios?.map((scenario, index) => {
        const modules = getScenarioModules(data, scenario);
        const scenarioTitle = scenario.isDefaultScenario ? 'Default scenario' : `Additional scenario ${index}`;
        return (
          <VersionHistorySection title={scenarioTitle} key={scenario.id}>
            <VersionHistoryScenarioGrid scenario={scenario} />

            {modules.map((m) => (
              <ModuleGridPdf
                key={m.moduleId}
                epdVersionId={data.epdVersionId}
                scenarioId={scenario.id}
                module={m.moduleId}
                gridCaption={m.dataGridCaption}
                moduleName={m.dataGridModuleName}
                moduleSectionTitle={
                  <PdfH2 style={{ fontSize: 12, color: '#000', textTransform: 'uppercase' }}>{m.descriptionLabel}</PdfH2>
                }
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={
                  data.epdDefinitionInformation.versionState === VersionStateType.Published ||
                  data.epdDefinitionInformation.versionState === VersionStateType.Deregistered
                }
              />
            ))}
          </VersionHistorySection>
        );
      }) ?? (
        <>
          <VersionHistorySummaryGridSkeleton skeletonRowsCount={7} />
        </>
      )}
    </>
  );
};

export default VersionHistoryScenariosSection;

export function getScenarioModules(data: EpdVersionHistoryData, scenario?: ScenarioItemModel) {
  if (!data || !scenario) {
    return [];
  }

  const possibleModules: (ScenarioModuleItem | false | null | undefined)[] = [
    data.systemBoundaryGrid.declaredModules?.a4 && {
      moduleId: 'A4',
      dataGridCaption: 'Module A4: Transport to the building site',
      dataGridModuleName: 'A4',
      descriptionLabel: 'Module A4',
    },
    data.systemBoundaryGrid.declaredModules?.a5 && {
      moduleId: 'A5',
      dataGridCaption: 'Module A5: Installation in the building',
      dataGridModuleName: 'A5',
      descriptionLabel: 'Module A5',
    },
    data.systemBoundaryGrid.declaredModules?.b1 && {
      moduleId: 'B1',
      dataGridCaption: 'Module B1: Use',
      dataGridModuleName: 'B1',
      descriptionLabel: 'Module B1',
    },
    data.systemBoundaryGrid.declaredModules?.b2 && {
      moduleId: 'B2',
      dataGridCaption: 'Module B2: Maintenance',
      dataGridModuleName: 'B2',
      descriptionLabel: 'Module B2',
    },
    data.systemBoundaryGrid.declaredModules?.b3 && {
      moduleId: 'B3',
      dataGridCaption: 'Module B3: Repair',
      dataGridModuleName: 'B3',
      descriptionLabel: 'Module B3',
    },
    data.systemBoundaryGrid.declaredModules?.b4 && {
      moduleId: 'B4',
      dataGridCaption: 'Module B4: Replacement',
      dataGridModuleName: 'B4',
      descriptionLabel: 'Module B4',
    },
    data.systemBoundaryGrid.declaredModules?.b5 && {
      moduleId: 'B5',
      dataGridCaption: 'Module B5: Refurbishment',
      dataGridModuleName: 'B5',
      descriptionLabel: 'Module B5',
    },
    data.systemBoundaryGrid.declaredModules?.b6 && {
      moduleId: 'B6',
      dataGridCaption: 'Module B6: Operational energy use',
      dataGridModuleName: 'B6',
      descriptionLabel: 'Module B6',
    },
    data.systemBoundaryGrid.declaredModules?.b7 && {
      moduleId: 'B7',
      dataGridCaption: 'Module B7: Operational water use',
      dataGridModuleName: 'B7',
      descriptionLabel: 'Module B7',
    },
    (data.systemBoundaryGrid.declaredModules?.c1 ||
      data.systemBoundaryGrid.declaredModules?.c2 ||
      data.systemBoundaryGrid.declaredModules?.c3 ||
      data.systemBoundaryGrid.declaredModules?.c4) && {
      moduleId: 'C1',
      dataGridCaption: 'Module C: End-of-life',
      dataGridModuleName: 'C',
      descriptionLabel: 'Module C',
    },
    scenario?.enabledRSL && {
      moduleId: 'RSL',
      dataGridCaption: 'Reference service life',
      dataGridModuleName: 'Reference service life',
      descriptionLabel: 'Reference service life',
    },
    data.systemBoundaryGrid.declaredModules?.d && {
      moduleId: 'D',
      dataGridCaption: 'Module D: Beyond product life cycle',
      dataGridModuleName: 'D',
      descriptionLabel: 'Module D',
    },
  ];

  const modules = possibleModules.filter((m) => m) as ScenarioModuleItem[];

  return modules;
}
